import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Insurance master + per-plan pricing tier (2026-07-17).
 *
 * Mirrors the Visa master 1:1. Pricing is captured as a child
 * `InsurancePricingTier` table - one row per (Insurance, plan tier).
 * All the pricing flow, snapshot, and payment-link paths reuse the visa
 * pattern verbatim - see recompute-totals.ts and markup-pricing.service.ts.
 */

export const InsuranceTypeSchema = z.enum([
  'SINGLE_TRIP',
  'ANNUAL_MULTI_TRIP',
  'STUDENT',
  'SENIOR',
  'GROUP',
  'ADVENTURE',
  'MEDICAL_ONLY',
]);
export type InsuranceType = z.infer<typeof InsuranceTypeSchema>;

export const InsuranceCoverageScopeSchema = z.enum([
  'DOMESTIC',
  'INTERNATIONAL',
  'WORLDWIDE',
  'WORLDWIDE_EXCLUDING_US_CANADA',
]);
export type InsuranceCoverageScope = z.infer<typeof InsuranceCoverageScopeSchema>;

export const InsuranceStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
]);
export type InsuranceStatus = z.infer<typeof InsuranceStatusSchema>;

/** A money amount: non-negative number with up to 2 decimal places. */
const PriceSchema = z.number().nonnegative();

// ---------------------------------------------------------------------------
// InsurancePricingTier (child of Insurance)
// ---------------------------------------------------------------------------

export const InsuranceMarkupKindSchema = z.enum(['PERCENT', 'FLAT']);
export type InsuranceMarkupKind = z.infer<typeof InsuranceMarkupKindSchema>;

export const InsurancePricingTierOutSchema = z.object({
  id: UuidSchema,
  insuranceId: UuidSchema,
  legacyId: z.number().int().nullable(),
  tierName: z.string(),
  coverageDays: z.number().int(),
  currencyId: UuidSchema.nullable(),
  currencyCode: z.string().nullable(),
  feePerApplicant: z.number(),
  agencyCharge: z.number(),
  markupType: InsuranceMarkupKindSchema.nullable(),
  markupValue: z.number(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type InsurancePricingTierOut = z.infer<typeof InsurancePricingTierOutSchema>;

const InsurancePricingTierCoreFieldsSchema = z.object({
  tierName: z.string().trim().min(1).max(80),
  coverageDays: z.number().int().min(0).max(3650).optional(),
  currencyId: UuidSchema.nullable().optional(),
  feePerApplicant: PriceSchema.optional(),
  agencyCharge: PriceSchema.optional(),
  markupType: InsuranceMarkupKindSchema.nullable().optional(),
  markupValue: PriceSchema.optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateInsurancePricingTierRequestSchema = InsurancePricingTierCoreFieldsSchema;
export type CreateInsurancePricingTierRequest = z.infer<
  typeof CreateInsurancePricingTierRequestSchema
>;

export const UpdateInsurancePricingTierRequestSchema =
  InsurancePricingTierCoreFieldsSchema.partial();
export type UpdateInsurancePricingTierRequest = z.infer<
  typeof UpdateInsurancePricingTierRequestSchema
>;

// ---------------------------------------------------------------------------
// Insurance (master)
// ---------------------------------------------------------------------------

export const InsurancePhotoSchema = z
  .object({
    /** Media-library asset id (preferred - survives presigned-URL expiry). */
    mediaId: z.string().uuid().optional(),
    /** Direct URL. Legacy imports only; new uploads store mediaId. */
    url: z.string().url().optional(),
    caption: z.string().max(200).nullable().optional(),
    alt: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((p) => Boolean(p.mediaId || p.url), { message: 'Photo needs a mediaId or a url' });
export type InsurancePhoto = z.infer<typeof InsurancePhotoSchema>;
export const InsurancePhotoListSchema = z.array(InsurancePhotoSchema);

export const InsuranceListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  legacyCode: z.string().nullable(),
  slug: z.string().nullable(),
  status: InsuranceStatusSchema,
  name: z.string(),
  insuranceType: InsuranceTypeSchema,
  coverageScope: InsuranceCoverageScopeSchema,
  maxCoverageDays: z.number().int(),
  // True = tier fee is a per-day rate (cost = fee * pax * tripDays); false =
  // flat per-applicant fee. See CreateInsuranceRequestSchema.
  perPersonPerDay: z.boolean(),
  coverageAmountLabel: z.string().nullable(),
  targetCountryId: UuidSchema.nullable(),
  targetCountryName: z.string(),
  minAge: z.number().int(),
  maxAge: z.number().int(),
  vendorSupplierId: UuidSchema.nullable(),
  vendorSupplierName: z.string().nullable(),
  pricingTierCount: z.number().int(),
  /**
   * Default pricing tier (lowest sortOrder among active tiers) surfaced on
   * the list row so the itinerary-builder picker can show a starting price
   * without a second round-trip per row. Null when the insurance has no
   * active tier yet.
   */
  defaultTierName: z.string().nullable(),
  /**
   * `feePerApplicant` of the default tier, FX-converted to INR paise so the
   * admin sees the same starting number the customer would eventually pay.
   * Null when no tier or the fee is zero. Agency-charge is intentionally
   * excluded here (it's a flat per-quote charge, not a per-traveller one).
   */
  defaultTierFeePerApplicantInrPaise: z.string().nullable(),
  isApprovedByAdmin: z.boolean(),
  photoCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type InsuranceListItem = z.infer<typeof InsuranceListItemSchema>;

export const InsuranceListQuerySchema = PaginationQuerySchema.extend({
  status: InsuranceStatusSchema.optional(),
  insuranceType: InsuranceTypeSchema.optional(),
  coverageScope: InsuranceCoverageScopeSchema.optional(),
  targetCountryId: UuidSchema.optional(),
  /**
   * Show every insurance whose target country is in the SAME region as this
   * country (data-driven via countries.region_id), plus any-country policies.
   * The itinerary builder passes the trip's destination country so a European
   * trip surfaces all European insurances without hard-coding a country list.
   * Takes precedence over `targetCountryId` when both are sent.
   */
  sameRegionAsCountryId: UuidSchema.optional(),
  vendorSupplierId: UuidSchema.optional(),
  isApprovedByAdmin: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type InsuranceListQuery = z.infer<typeof InsuranceListQuerySchema>;

export const InsuranceListResponseSchema = paginatedResponseSchema(InsuranceListItemSchema);
export type InsuranceListResponse = z.infer<typeof InsuranceListResponseSchema>;

export const InsuranceDetailSchema = InsuranceListItemSchema.extend({
  requiredDocuments: z.array(z.string()),
  eligibleNationalities: z.array(z.string()),
  excludedNationalities: z.array(z.string()),
  contractRef: z.string().nullable(),
  policyNumberFormat: z.string().nullable(),
  shortDescription: z.string().nullable(),
  longDescriptionHtml: z.string().nullable(),
  termsHtml: z.string().nullable(),
  cancellationHtml: z.string().nullable(),
  claimProcedureHtml: z.string().nullable(),
  validityFrom: z.string().nullable(),
  validityTo: z.string().nullable(),
  photos: z.unknown().nullable(),
  isApprovedByModerator: z.boolean(),
  lastApprovedAt: IsoDateTimeSchema.nullable(),
  metaTagName: z.string().nullable(),
  pricingTiers: z.array(InsurancePricingTierOutSchema),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type InsuranceDetail = z.infer<typeof InsuranceDetailSchema>;

const InsuranceCoreFieldsSchema = z.object({
  status: InsuranceStatusSchema.optional(),
  legacyCode: z.string().trim().max(40).nullable().optional(),
  slug: z.string().trim().min(1).max(160).nullable().optional(),
  // Identity
  name: z.string().trim().min(1).max(300),
  insuranceType: InsuranceTypeSchema.optional(),
  coverageScope: InsuranceCoverageScopeSchema.optional(),
  maxCoverageDays: z.number().int().min(0).max(3650).optional(),
  // When true, the pricing tier's feePerApplicant is a per-DAY rate and the
  // itinerary cost = fee * pax * tripDays + agencyCharge. Default false = the
  // legacy flat per-applicant fee.
  perPersonPerDay: z.boolean().optional(),
  coverageAmountLabel: z.string().trim().max(120).nullable().optional(),
  // Target
  targetCountryId: UuidSchema.nullable().optional(),
  targetCountryName: z.string().trim().max(120).optional(),
  // Age eligibility
  minAge: z.number().int().min(0).max(120).optional(),
  maxAge: z.number().int().min(0).max(120).optional(),
  // Documentation
  requiredDocuments: z.array(z.string().trim().min(1).max(200)).optional(),
  eligibleNationalities: z.array(z.string().trim().min(2).max(3).toUpperCase()).optional(),
  excludedNationalities: z.array(z.string().trim().min(2).max(3).toUpperCase()).optional(),
  // Vendor / underwriter
  vendorSupplierId: UuidSchema.nullable().optional(),
  contractRef: z.string().trim().max(120).nullable().optional(),
  policyNumberFormat: z.string().trim().max(120).nullable().optional(),
  // Content
  shortDescription: z.string().max(1000).nullable().optional(),
  longDescriptionHtml: z.string().max(50_000).nullable().optional(),
  termsHtml: z.string().max(20_000).nullable().optional(),
  cancellationHtml: z.string().max(20_000).nullable().optional(),
  claimProcedureHtml: z.string().max(20_000).nullable().optional(),
  // Validity
  validityFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  validityTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  // Media
  photos: InsurancePhotoListSchema.nullable().optional(),
  // Misc
  metaTagName: z.string().trim().max(200).nullable().optional(),
});

export const CreateInsuranceRequestSchema = InsuranceCoreFieldsSchema;
export type CreateInsuranceRequest = z.infer<typeof CreateInsuranceRequestSchema>;

export const UpdateInsuranceRequestSchema = InsuranceCoreFieldsSchema.partial();
export type UpdateInsuranceRequest = z.infer<typeof UpdateInsuranceRequestSchema>;

export const InsuranceApprovalRequestSchema = z.object({
  tier: z.enum(['admin', 'moderator']),
  approve: z.boolean(),
});
export type InsuranceApprovalRequest = z.infer<typeof InsuranceApprovalRequestSchema>;
